/**
 * Combos Page - Main Component
 * Refactored with Clean Architecture pattern
 * Enhanced with discount functionality
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useInstance } from '../../contexts/InstanceContext';
import { toMinor } from '../../lib/currency';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Modal } from '../../components/ui/modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import { Plus, Pencil, Trash2, Utensils, X, Package, Percent, DollarSign, Tag } from 'lucide-react';
import { FilterPanel } from '../../components/ui/FilterPanel';

import './styles.css';

export default function Combos() {
  const { t } = useTranslation();
  const { formatCurrency } = useInstance();
  
  const [combos, setCombos] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    items: [],
    image_url: '',
    is_active: true,
    discount_type: 'none', // none, percent, value
    discount_value: '',
    category_id: '',
  });
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedVariation, setSelectedVariation] = useState('');
  const [productQty, setProductQty] = useState('1');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [combosRes, productsRes, categoriesRes] = await Promise.all([
        api.get('/combos'),
        api.get('/products'),
        api.get('/categories?type=combo'),
      ]);
      setCombos(combosRes.data);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data || []);
    } catch (err) {
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  // Get category name by ID (including path for nested)
  const getCategoryName = (id) => {
    if (!id) return '';
    const cat = categories.find(c => c.id === id);
    if (!cat) return '';
    
    // Build path
    const paths = [cat.name];
    let current = cat;
    while (current.parent_id) {
      current = categories.find(c => c.id === current.parent_id);
      if (current) paths.unshift(current.name);
    }
    return paths.join(' > ');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Calculate final price based on discount
      const originalPrice = calculateOriginalPrice(formData.items);
      let finalPrice = parseFloat(formData.price) || 0;
      
      // If price is empty, calculate from items with discount
      if (!formData.price && originalPrice > 0) {
        if (formData.discount_type === 'percent' && formData.discount_value) {
          finalPrice = originalPrice * (1 - parseFloat(formData.discount_value) / 100);
        } else if (formData.discount_type === 'value' && formData.discount_value) {
          finalPrice = originalPrice - parseFloat(formData.discount_value);
        } else {
          finalPrice = originalPrice;
        }
      }

      const data = {
        name: formData.name,
        description: formData.description,
        price_minor: toMinor(Math.max(0, finalPrice)),
        items: formData.items.map(item => ({
          ...item,
          unit_price_minor: toMinor(item.unit_price || item.price || 0),
        })),
        image_url: formData.image_url,
        is_active: formData.is_active,
        category_id: formData.category_id || null,
      };

      if (editing) {
        await api.put(`/combos/${editing.id}`, data);
        toast.success('Combo atualizado com sucesso!');
      } else {
        await api.post('/combos', data);
        toast.success('Combo criado com sucesso!');
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Confirma exclusão deste combo?')) return;
    try {
      await api.delete(`/combos/${id}`);
      toast.success('Combo excluído com sucesso!');
      fetchData();
    } catch (err) {
      toast.error(t('errors.generic'));
    }
  };

  const handleEdit = (combo) => {
    setEditing(combo);
    const originalPrice = calculateOriginalPrice(combo.items || []);
    const priceDiff = originalPrice - combo.price;
    const percentDiff = originalPrice > 0 ? ((priceDiff / originalPrice) * 100).toFixed(0) : 0;
    
    setFormData({
      name: combo.name,
      description: combo.description || '',
      price: combo.price.toString(),
      items: combo.items || [],
      image_url: combo.image_url || '',
      is_active: combo.is_active ?? true,
      discount_type: priceDiff > 0 ? 'value' : 'none',
      discount_value: priceDiff > 0 ? priceDiff.toFixed(2) : '',
      category_id: combo.category_id || '',
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      items: [],
      image_url: '',
      is_active: true,
      discount_type: 'none',
      discount_value: '',
      category_id: '',
    });
    setSelectedProduct('');
    setSelectedVariation('');
    setProductQty('1');
  };

  // Handle modal close - reset form when closing
  const handleModalOpenChange = (open) => {
    setShowModal(open);
    if (!open) {
      setTimeout(() => resetForm(), 100);
    }
  };

  const getSelectedProductVariations = () => {
    if (!selectedProduct) return [];
    const product = products.find(p => p.id === selectedProduct);
    return product?.variations || [];
  };

  const addItemToCombo = () => {
    if (!selectedProduct) return;
    const product = products.find(p => p.id === selectedProduct);
    if (!product) return;

    const variation = selectedVariation && selectedVariation !== 'none'
      ? product.variations?.find(v => v.id === selectedVariation || v.name === selectedVariation) 
      : null;
    
    const itemKey = variation ? `${selectedProduct}-${selectedVariation}` : selectedProduct;
    const existing = formData.items.find(i => 
      i.product_id === selectedProduct && i.variation_id === (variation?.id || null)
    );
    
    if (existing) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.map(i =>
          i.product_id === selectedProduct && i.variation_id === (variation?.id || null)
            ? { ...i, quantity: i.quantity + parseInt(productQty) }
            : i
        ),
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, {
          product_id: selectedProduct,
          variation_id: variation?.id || null,
          variation_name: variation?.name || null,
          quantity: parseInt(productQty) || 1,
        }],
      }));
    }

    setSelectedProduct('');
    setSelectedVariation('');
    setProductQty('1');
  };

  const removeItemFromCombo = (productId, variationId = null) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(i => !(i.product_id === productId && i.variation_id === variationId)),
    }));
  };

  const getProductName = (id) => products.find(p => p.id === id)?.name || 'Produto não encontrado';
  const getProductPrice = (id) => products.find(p => p.id === id)?.price || 0;

  const calculateOriginalPrice = (items) => {
    return items.reduce((sum, item) => sum + (getProductPrice(item.product_id) * item.quantity), 0);
  };

  const calculateFinalPrice = () => {
    const originalPrice = calculateOriginalPrice(formData.items);
    if (formData.discount_type === 'percent' && formData.discount_value) {
      return originalPrice * (1 - parseFloat(formData.discount_value) / 100);
    } else if (formData.discount_type === 'value' && formData.discount_value) {
      return originalPrice - parseFloat(formData.discount_value);
    }
    return parseFloat(formData.price) || originalPrice;
  };

  const calculateSavings = () => {
    const originalPrice = calculateOriginalPrice(formData.items);
    const finalPrice = calculateFinalPrice();
    return originalPrice - finalPrice;
  };

  // Apply filters
  const filteredCombos = combos.filter(combo => {
    if (filters.name && !combo.name.toLowerCase().includes(filters.name.toLowerCase())) {
      return false;
    }
    if (filters.status && filters.status !== 'all') {
      if (filters.status === 'active' && !combo.is_active) return false;
      if (filters.status === 'inactive' && combo.is_active) return false;
    }
    if (filters.category_id && filters.category_id !== 'all') {
      if (combo.category_id !== filters.category_id) return false;
    }
    if (filters.product_id && filters.product_id !== 'all') {
      const hasProduct = combo.items?.some(item => item.product_id === filters.product_id);
      if (!hasProduct) return false;
    }
    return true;
  });

  const filterDefinitions = [
    {
      key: 'name',
      label: 'Nome',
      type: 'text',
      placeholder: 'Buscar combo...',
    },
    {
      key: 'category_id',
      label: 'Categoria',
      type: 'select',
      placeholder: 'Todas',
      options: [
        { value: 'all', label: 'Todas' },
        ...categories.filter(c => c.is_active).map(c => ({ value: c.id, label: c.name })),
      ],
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      placeholder: 'Todos',
      options: [
        { value: 'all', label: 'Todos' },
        { value: 'active', label: 'Ativo' },
        { value: 'inactive', label: 'Inativo' },
      ],
    },
    {
      key: 'product_id',
      label: 'Contém Produto',
      type: 'select',
      placeholder: 'Todos',
      options: [
        { value: 'all', label: 'Todos' },
        ...products.filter(p => p.is_active).map(p => ({ value: p.id, label: p.name })),
      ],
    },
  ];

  return (
    <div className="combos-page" data-testid="combos-page">
      <div className="combos-header">
        <div className="combos-header-title">
          <h1>
            <Utensils className="h-6 w-6" />
            {t('combos.title')}
          </h1>
          <p>Crie combinações de produtos com preço especial</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} data-testid="new-combo-btn">
          <Plus className="h-4 w-4 mr-2" />
          {t('combos.newCombo')}
        </Button>
      </div>

      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={setFilters}
        onSearch={() => {}}
        onClear={() => setFilters({})}
        showExport={false}
      />

      <div className="combos-grid">
        {loading ? (
          <Card className="col-span-full">
            <CardContent className="py-8 text-center">{t('common.loading')}</CardContent>
          </Card>
        ) : filteredCombos.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="py-8 text-center text-muted-foreground">
              {t('combos.noCombos')}
            </CardContent>
          </Card>
        ) : (
          filteredCombos.map(combo => (
            <Card key={combo.id} className="combo-card" data-testid={`combo-card-${combo.id}`}>
              <CardHeader>
                <div className="combo-card-header">
                  <div className="combo-card-info">
                    <div className="combo-icon">
                      <Utensils className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{combo.name}</CardTitle>
                      <CardDescription>
                        {combo.items?.length || 0} itens
                        {combo.category_id && (
                          <span className="ml-2 text-primary">• {getCategoryName(combo.category_id)}</span>
                        )}
                      </CardDescription>
                    </div>
                  </div>
                  <Badge variant={combo.is_active ? 'default' : 'secondary'}>
                    {combo.is_active ? t('common.active') : t('common.inactive')}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {combo.description && (
                  <p className="text-sm text-muted-foreground mb-3">{combo.description}</p>
                )}
                <div className="combo-price">
                  <span className="combo-price-current">
                    {formatCurrency(combo.price)}
                  </span>
                  {combo.items?.length > 0 && calculateOriginalPrice(combo.items) > combo.price && (
                    <>
                      <span className="combo-price-original">
                        {formatCurrency(calculateOriginalPrice(combo.items))}
                      </span>
                      <Badge variant="destructive" className="ml-2">
                        -{formatCurrency(calculateOriginalPrice(combo.items) - combo.price)}
                      </Badge>
                    </>
                  )}
                </div>
                <div className="combo-actions">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => handleEdit(combo)}>
                    <Pencil className="h-4 w-4 mr-1" />
                    Editar
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(combo.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Modal 
        open={showModal} 
        onOpenChange={handleModalOpenChange}
        title={editing ? t('combos.editCombo') : t('combos.newCombo')}
        description={editing ? 'Atualize as informações do combo' : 'Preencha os dados do novo combo'}
        size="lg"
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => handleModalOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" form="combo-form" data-testid="save-combo-btn">{t('common.save')}</Button>
          </>
        }
      >
        <form id="combo-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t('combos.name')} *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
              required
              data-testid="combo-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">{t('combos.description')}</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
            />
          </div>

          {/* Category and Subcategory */}
          <div className="space-y-2">
            <Label>Categoria</Label>
            <Select 
              value={formData.category_id} 
              onValueChange={(v) => setFormData(p => ({ ...p, category_id: v }))}
            >
              <SelectTrigger data-testid="combo-category-select">
                <SelectValue placeholder="Selecione uma categoria" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.parent_id ? `↳ ${cat.name}` : cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="image_url">URL da Imagem</Label>
            <Input
              id="image_url"
              type="url"
              value={formData.image_url}
              onChange={(e) => setFormData(p => ({ ...p, image_url: e.target.value }))}
              placeholder="https://..."
            />
          </div>

          {/* Items Section */}
          <div className="combo-items-section">
            <Label className="mb-2 block">Itens do Combo</Label>
            
            <div className="combo-add-item">
              <Select value={selectedProduct} onValueChange={(val) => { setSelectedProduct(val); setSelectedVariation(''); }}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Selecione um produto" />
                </SelectTrigger>
                <SelectContent>
                  {products.filter(p => p.is_active).map(product => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name} - {formatCurrency(product.price)}
                      {product.variations?.length > 0 && ' (com variações)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {getSelectedProductVariations().length > 0 && (
                <Select value={selectedVariation} onValueChange={setSelectedVariation}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Variação" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem variação</SelectItem>
                    {getSelectedProductVariations().map((variation, idx) => (
                      <SelectItem key={variation.id || idx} value={variation.id || variation.name}>
                        {variation.name} {variation.price_modifier > 0 && `(+${formatCurrency(variation.price_modifier)})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Input
                type="number"
                min="1"
                value={productQty}
                onChange={(e) => setProductQty(e.target.value)}
                className="w-20"
                placeholder="Qtd"
              />
              <Button type="button" variant="outline" onClick={addItemToCombo}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            <ScrollArea className="h-32">
              <div className="space-y-2">
                {formData.items.map((item, idx) => (
                  <div key={`${item.product_id}-${item.variation_id || idx}`} className="combo-item">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Package className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm truncate">
                        {getProductName(item.product_id)}
                        {item.variation_name && <span className="text-muted-foreground"> ({item.variation_name})</span>}
                      </span>
                      <Badge variant="secondary" className="flex-shrink-0">{item.quantity}x</Badge>
                      <span className="text-xs text-muted-foreground flex-shrink-0">
                        {formatCurrency(getProductPrice(item.product_id) * item.quantity)}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 flex-shrink-0"
                      onClick={() => removeItemFromCombo(item.product_id, item.variation_id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {formData.items.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Nenhum item adicionado
                  </p>
                )}
              </div>
            </ScrollArea>

            {formData.items.length > 0 && (
              <div className="combo-items-total">
                <span>Valor original dos itens:</span>
                <span className="font-mono font-bold">{formatCurrency(calculateOriginalPrice(formData.items))}</span>
              </div>
            )}
          </div>

          <Separator />

          {/* Discount Section */}
          {formData.items.length > 0 && (
            <div className="space-y-3 p-3 bg-primary/5 rounded-lg border">
              <Label className="flex items-center gap-2">
                <Tag className="h-4 w-4" />
                Desconto do Combo
              </Label>
              
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={formData.discount_type === 'none' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFormData(p => ({ ...p, discount_type: 'none', discount_value: '', price: '' }))}
                >
                  Sem Desconto
                </Button>
                <Button
                  type="button"
                  variant={formData.discount_type === 'percent' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFormData(p => ({ ...p, discount_type: 'percent', price: '' }))}
                >
                  <Percent className="h-3 w-3 mr-1" />
                  Percentual
                </Button>
                <Button
                  type="button"
                  variant={formData.discount_type === 'value' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFormData(p => ({ ...p, discount_type: 'value', price: '' }))}
                >
                  <DollarSign className="h-3 w-3 mr-1" />
                  Valor Fixo
                </Button>
              </div>

              {formData.discount_type !== 'none' && (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    placeholder={formData.discount_type === 'percent' ? 'Ex: 10' : 'Ex: 5.00'}
                    value={formData.discount_value}
                    onChange={(e) => setFormData(p => ({ ...p, discount_value: e.target.value, price: '' }))}
                    className="flex-1"
                    data-testid="combo-discount-input"
                  />
                  <span className="text-sm text-muted-foreground">
                    {formData.discount_type === 'percent' ? '%' : 'R$'}
                  </span>
                </div>
              )}

              {/* Price Summary */}
              <div className="mt-3 p-2 bg-background rounded border space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Valor Original:</span>
                  <span className="line-through">{formatCurrency(calculateOriginalPrice(formData.items))}</span>
                </div>
                {calculateSavings() > 0 && (
                  <div className="flex justify-between text-sm text-destructive">
                    <span>Economia:</span>
                    <span>- {formatCurrency(calculateSavings())}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold">
                  <span>Preço Final:</span>
                  <span className="text-primary">{formatCurrency(Math.max(0, calculateFinalPrice()))}</span>
                </div>
              </div>
            </div>
          )}

          {/* Manual Price Override */}
          {formData.items.length === 0 && (
            <div className="space-y-2">
              <Label htmlFor="price">{t('combos.price')} *</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData(p => ({ ...p, price: e.target.value }))}
                required={formData.items.length === 0}
                data-testid="combo-price-input"
              />
            </div>
          )}

          <div className="flex items-center gap-2">
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(v) => setFormData(p => ({ ...p, is_active: v }))}
            />
            <Label htmlFor="is_active">{t('common.active')}</Label>
          </div>
        </form>
      </Modal>
    </div>
  );
}
